import React, { useState, useEffect } from "react";
import Pagination from "../../components/Pagination.tsx";
import { useTheme } from "../../contexts/ThemeContext.tsx";
import { getTheme } from '../../theme.ts';
import { searchGlobal, SearchResultItem, ContactMetadata } from "../../services/Search.ts";
import Loader from "../../components/Loader.tsx";
import { FaSearch } from "react-icons/fa";

const AdminUserContacts: React.FC = () => {
  const { themeName } = useTheme();
  const currentTheme = getTheme(themeName);

  // State for data and pagination
  const [contacts, setContacts] = useState<SearchResultItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const pageSize = 10;

  // Get token (assuming stored in localStorage as in other files)
  const rawData = localStorage.getItem('user');
  const cred = rawData ? JSON.parse(rawData) : null;
  const token = cred?.access;

  const fetchContacts = async () => {
    setLoading(true);
    try {
      const params: any = {
        type: 'contact',
        page: currentPage,
        page_size: pageSize
      };

      if (searchQuery) {
        params.q = searchQuery;
      }

      const response = await searchGlobal(token, params) as any;

      if (response && response.data) {
        setContacts(response.data.results);
        setTotalCount(response.data.count);
        // Calculate total pages based on count and page size
        setTotalPages(Math.ceil(response.data.count / pageSize));
      } else {
        console.error("Failed to fetch contacts");
      }
    } catch (err) {
      console.error("Error fetching contacts:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Debounce search to avoid too many API calls
    const timeoutId = setTimeout(() => {
      if (currentPage === 1) {
        fetchContacts();
      } else {
        setCurrentPage(1); // will trigger useEffect below
      }
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [searchQuery]);

  useEffect(() => {
    fetchContacts();
  }, [currentPage]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  if (loading && currentPage === 1 && !contacts.length) {
    return <Loader />;
  }

  return (
    <div style={{ backgroundColor: currentTheme.background, padding: '24px' }}>
      <div style={{ width: '100%', backgroundColor: currentTheme.surface, boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)', borderRadius: '8px', overflow: 'hidden' }}>

        {/* Search Header */}
        <div style={{ padding: '16px', borderBottom: `1px solid ${currentTheme.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h2 style={{ fontSize: '18px', fontWeight: 'bold', color: currentTheme.text }}>User Contacts</h2>
          <div style={{ position: 'relative', width: '300px' }}>
            <FaSearch style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: currentTheme.textSecondary }} />
            <input
              type="text"
              placeholder="Search contacts..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{
                width: '100%',
                padding: '8px 12px 8px 36px',
                borderRadius: '6px',
                border: `1px solid ${currentTheme.border}`,
                backgroundColor: currentTheme.background,
                color: currentTheme.text,
                outline: 'none'
              }}
            />
          </div>
        </div>

        <div style={{ overflow: 'auto', maxHeight: 'calc(100vh - 200px)' }}>
          <table style={{ width: '100%', textAlign: 'left', borderCollapse: 'collapse' }}>
            <thead style={{ backgroundColor: currentTheme.surface, color: currentTheme.textSecondary, borderBottom: `1px solid ${currentTheme.border}` }}>
              <tr>
                <th style={{ padding: '12px', fontWeight: '600', fontSize: '14px', width: '50px' }}>#</th>
                <th style={{ padding: '12px', fontWeight: '600', fontSize: '14px' }}>Name</th>
                <th style={{ padding: '12px', fontWeight: '600', fontSize: '14px' }}>Email</th>
                <th style={{ padding: '12px', fontWeight: '600', fontSize: '14px' }}>Message</th>
                <th style={{ padding: '12px', fontWeight: '600', fontSize: '14px' }}>Phone</th>
              </tr>
            </thead>
            <tbody>
              {contacts.length === 0 && !loading ? (
                <tr>
                  <td colSpan={5} style={{ padding: '24px', textAlign: 'center', color: currentTheme.textSecondary }}>
                    No contact requests found.
                  </td>
                </tr>
              ) : (
                contacts.map((item, index) => {
                  const metadata = item.metadata as ContactMetadata;
                  return (
                    <tr
                      key={item.id}
                      style={{
                        borderBottom: `1px solid ${currentTheme.border}`,
                        backgroundColor: index % 2 === 0 ? currentTheme.surface : currentTheme.background
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = currentTheme.hover}
                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = index % 2 === 0 ? currentTheme.surface : currentTheme.background}
                    >
                      <td style={{ padding: '8px 12px', color: currentTheme.text, fontSize: '14px' }}>
                        {(currentPage - 1) * pageSize + index + 1}
                      </td>
                      <td style={{ padding: '8px 12px', color: currentTheme.text, fontSize: '14px' }}>{item.title}</td>
                      <td style={{ padding: '8px 12px', color: currentTheme.text, fontSize: '14px' }}>{metadata.email}</td>
                      <td style={{ padding: '8px 12px', color: currentTheme.text, fontSize: '14px' }}>{(metadata as any).message || '-'}</td>
                      <td style={{ padding: '8px 12px', color: currentTheme.text, fontSize: '14px' }}>{metadata.phone}</td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination Section */}
        <div style={{ padding: '12px 16px', display: 'flex', justifyContent: 'flex-end', borderTop: `1px solid ${currentTheme.border}` }}>
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={handlePageChange}
          />
        </div>
      </div>
    </div>
  );
};

export default AdminUserContacts;
